import type { Book, Chapter } from "#/types";

import {
  cancelJob as apiCancelJob,
  checkUpdates as apiCheckUpdates,
  deleteBook as apiDeleteBook,
  deleteChapters as apiDeleteChapters,
  startDownload as apiStartDownload,
  getBook,
  getChapters,
} from "#/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function useBookDetail(id: string | undefined) {
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | undefined>(undefined);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [nextCursor, setNextCursor] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [fetchingChapters, setFetchingChapters] = useState(false);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // EPUB settings
  const [isEpubDialogOpen, setIsEpubDialogOpen] = useState(false);

  // Download settings
  const [useExtension, setUseExtension] = useState(false);
  const [downloadLimit, setDownloadLimit] = useState<number | undefined>(100);

  const fetchBook = async () => {
    if (!id) {
      return;
    }
    try {
      const bookData = await getBook(id);
      setBook(bookData);
      setUseExtension(bookData.needs_extension);
    } catch (error) {
      console.error("Failed to fetch book:", error);
      void navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (cursor?: number) => {
    if (!id || fetchingChapters) {
      return;
    }
    setFetchingChapters(true);
    try {
      const data = await getChapters(id, {
        cursor,
        limit: 100,
        desc: sortOrder === "desc",
      });
      if (cursor) {
        setChapters((prev) => [...prev, ...data.items]);
      } else {
        setChapters(data.items);
      }
      setNextCursor(data.next_cursor ?? undefined);
    } catch (error) {
      console.error("Failed to fetch chapters:", error);
    } finally {
      setFetchingChapters(false);
    }
  };

  useEffect(() => {
    void fetchBook();
  }, [id]);

  useEffect(() => {
    void fetchChapters();
  }, [id, sortOrder]);

  const checkUpdates = async () => {
    if (!id || !book) {
      return;
    }
    setCheckingUpdates(true);
    try {
      const updatedBook = await apiCheckUpdates(id);
      setBook({ ...book, ...updatedBook });
    } catch (error) {
      console.error("Failed to check updates:", error);
    } finally {
      setCheckingUpdates(false);
    }
  };

  const startDownload = async () => {
    if (!id) {
      return;
    }
    try {
      await apiStartDownload(id, useExtension, downloadLimit);
      void fetchBook();
    } catch (error) {
      console.error("Failed to start download:", error);
      alert(`Failed to start download: ${String(error)}`);
    }
  };

  const cancelDownload = async () => {
    if (!id || !book?.is_downloading) {
      return;
    }
    try {
      await apiCancelJob(parseInt(id));
      void fetchBook();
    } catch (error) {
      console.error("Failed to cancel download:", error);
      alert(`Failed to cancel download: ${String(error)}`);
    }
  };

  const deleteChaptersFrom = async (chapterId: number) => {
    if (!id) {
      return;
    }
    if (
      !globalThis.confirm(
        "Are you sure you want to delete this chapter and ALL subsequent chapters?",
      )
    ) {
      return;
    }
    try {
      await apiDeleteChapters(id, chapterId);
      void fetchBook();
      void fetchChapters();
    } catch (error) {
      console.error("Failed to delete chapters:", error);
    }
  };

  const removeBook = async () => {
    if (!id) {
      return;
    }
    if (!globalThis.confirm("Are you sure you want to remove this book from your library?")) {
      return;
    }
    try {
      await apiDeleteBook(id);
      void navigate("/");
    } catch (error) {
      console.error("Failed to remove book:", error);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  return {
    book,
    chapters,
    nextCursor,
    loading,
    fetchingChapters,
    checkingUpdates,
    sortOrder,
    isEpubDialogOpen,
    useExtension,
    downloadLimit,
    setIsEpubDialogOpen,
    setUseExtension,
    setDownloadLimit,
    checkUpdates,
    startDownload,
    cancelDownload,
    deleteChaptersFrom,
    removeBook,
    toggleSortOrder,
    fetchChapters,
  };
}
